export type WorkoutBuilderStepInputType = "single_select" | "body_area_list";

export type WorkoutBuilderStepKey =
  | "condition"
  | "bodyArea"
  | "feel";

export type WorkoutBuilderImageReference = {
  id: string;
  label: string;
  imageURL?: string;
  options?: string[];
};

export type WorkoutBuilderStep = {
  key: WorkoutBuilderStepKey;
  title: string;
  iconName?: string;
  question: string;
  helperText?: string;
  inputType: WorkoutBuilderStepInputType;
  options: string[];
  imageReferences?: WorkoutBuilderImageReference[];
};

export type WorkoutBuilderConfig = {
  enabled: boolean;
  buildButtonLabel: string;
  steps: WorkoutBuilderStep[];
};

const buildBodyAreaOptions = (
  imageReferences: WorkoutBuilderImageReference[] = []
) =>
  Array.from(
    new Set(
      imageReferences.flatMap((reference) => reference.options || [])
    )
  );

export const DEFAULT_WORKOUT_BUILDER_CONFIG: WorkoutBuilderConfig = {
  enabled: true,
  buildButtonLabel: "Build My Program",
  steps: [
    {
      key: "condition",
      title: "Step 1 - What's Going On",
      iconName: "pulse-outline",
      question: "What's going on with your body?",
      helperText: "Used as the primary decision signal for the workout flow.",
      inputType: "single_select",
      options: [
        "Pain or discomfort",
        "Tight or restricted",
        "Weak or unstable",
        "I want to move better",
      ],
    },
    {
      key: "bodyArea",
      title: "Step 2 - Select Area",
      iconName: "body-outline",
      question: "Where do you feel it?",
      helperText:
        "Use your front/back body image as guidance. The body can stay non-clickable if the list is labeled.",
      inputType: "body_area_list",
      options: [
        "Neck",
        "Shoulders",
        "Front Hips",
        "Knees",
        "Shins",
        "Ankles",
        "Feet",
        "Upper back",
        "Middle back",
        "Low back",
        "Outer Hips",
        "Glutes",
        "SI Joint",
      ],
      imageReferences: [
        {
          id: "front",
          label: "Front Body Reference",
          imageURL: "",
          options: [
            "Neck",
            "Shoulders",
            "Front Hips",
            "Knees",
            "Shins",
            "Ankles",
            "Feet",
          ],
        },
        {
          id: "back",
          label: "Back Body Reference",
          imageURL: "",
          options: [
            "Neck",
            "Shoulders",
            "Upper back",
            "Middle back",
            "Low back",
            "Outer Hips",
            "Glutes",
            "SI Joint",
          ],
        },
      ],
    },
    {
      key: "feel",
      title: "Step 3 - What Does It Feel Like",
      iconName: "alert-circle-outline",
      question: "What does it feel like?",
      inputType: "single_select",
      options: [
        "Tight or stiff",
        "Weak or unstable",
        "Sharp or pinching",
        "Achy or sore",
        "Hard to move normally",
        "Not sure",
      ],
    },
  ],
};

export const cloneWorkoutBuilderConfig = (
  config: WorkoutBuilderConfig = DEFAULT_WORKOUT_BUILDER_CONFIG
): WorkoutBuilderConfig => ({
  ...config,
  steps: DEFAULT_WORKOUT_BUILDER_CONFIG.steps.map((defaultStep) => {
    const savedStep = (config.steps || []).find(
      (candidate) => candidate.key === defaultStep.key
    );
    const mergedStep = {
      ...defaultStep,
      ...savedStep,
    } as WorkoutBuilderStep;
    const imageReferences = (
      mergedStep.imageReferences ||
      defaultStep?.imageReferences ||
      []
    ).map((reference) => ({
      ...reference,
      options: [...(reference.options || [])],
    }));
    const options =
      mergedStep.inputType === "body_area_list" &&
      imageReferences.some((reference) => (reference.options || []).length > 0)
        ? buildBodyAreaOptions(imageReferences)
        : [...(mergedStep.options || defaultStep?.options || [])];

    return {
      key: mergedStep.key,
      title: mergedStep.title,
      iconName: mergedStep.iconName,
      question: mergedStep.question,
      helperText: mergedStep.helperText,
      inputType: mergedStep.inputType,
      options,
      imageReferences,
    };
  }),
});
